import { SyntheticEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { Input, Select, DatePicker, DateTools, TFileWithType, Button, RadioList } from '@treezor/design-system';

import { ContentLayout, TwoColumns, CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { useCreateUser } from '@/features/user/hooks/createUser/useCreateUser';
import {
  CreateUserTaxResidences,
  defaultTaxResidence,
} from '@/features/user/components/CreateUser/CreateUserTaxResidences';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { USResident } from '@/features/user/components/CreateUser/USResident';
import { useUserInfo } from '@/features/user/hooks/useUserInfo';
import { ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';
import { EParentType } from '@/features/user/store/user.model';
import { walletStatusList } from '@/features/wallets/store/models/wallet.model';
import { getWallets } from '@/features/wallets/store/actions/wallet.actions';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { selectWalletsByUserId } from '@/store/selectors';
import { CreateUserKycDocuments } from '@/features/user/components/CreateUser/CreateUserKycDocuments';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

import { minimumDate } from '@/hooks/useDashboardConstants';

export const kycDefaultValues = {
  birthday: undefined,
  placeOfBirth: '',
  nationality: undefined,
  nationalityOther: undefined,
  birthCountry: undefined,
  specifiedUSPerson: 0,
  taxResidences: undefined,
  kycDocuments: undefined,
  personalAssets: undefined,
  incomeRange: undefined,
  occupationType: undefined,
  occupationCategory: undefined,
  personalAssetsRange: undefined,
  monthlyIncomeRange: undefined,
};

export function CreateUserPhysicalVerificationDetails({ stepsLength }: ICreateUserPartProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    config,
    handleSubmit,
    watches,
    register,
    user,
    language,
    handleInputChange,
    handleValueChange,
    handleDateChange,
    shouldBeUSResident,
    parentUser,
  } = useCreateUser({ ...kycDefaultValues });

  const parentWallets = useAppSelector((state) => selectWalletsByUserId(state, parentUser?.userId), shallowEqual);
  const { isTaxResidenceDisabled } = useAddTaxResidences();

  const { isKycDocumentsInvalid } = useCreateUserKycDocuments();

  const { isPhysicalUser, isSelfEmployed } = useUserInfo(parentUser);
  const isParentPhysical = isPhysicalUser && !isSelfEmployed;

  const {
    countryCodeOptions,
    personalAssetOptions,
    incomeRangeOptions,
    occupationTypeOptions,
    personalAssetRanges,
    monthlyIncomeRangeOptions,
    occupationCategoryOptions,
  } = useFieldOptions();

  const birthdayWatch = watches['birthday'];
  const taxResidencesWatch = watches['taxResidences'];
  const specifiedUSPersonWatch = watches['specifiedUSPerson'];
  const kycDocumentsWatch = watches['kycDocuments'];
  const selectedDate = DateTools.localStringToDate(birthdayWatch, language);

  useEffect(() => {
    if (isParentPhysical) {
      dispatch(getWallets(parentUser?.userId, false));
    }
  }, [dispatch, isParentPhysical, parentUser?.userId]);

  useEffect(() => {
    if (isParentPhysical) {
      if (
        parentWallets?.length &&
        parentWallets
          .filter((wallet) => wallet.walletStatus === walletStatusList.VALIDATED)
          ?.every((wallet) => wallet.walletTypeId === 10) &&
        birthdayWatch &&
        DateTools.difference(DateTools.now(), DateTools.localStringToDate(birthdayWatch, language), 'year') < 18
      ) {
        handleValueChange(EParentType.shareholder, 'parentType');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birthdayWatch, isParentPhysical, parentWallets]);

  const isFormDisabled = useMemo(() => {
    return (
      (!!selectedDate && DateTools.compare(new Date(), selectedDate) === 1) ||
      isTaxResidenceDisabled(taxResidencesWatch) ||
      isKycDocumentsInvalid(kycDocumentsWatch)
    );
  }, [selectedDate, isTaxResidenceDisabled, taxResidencesWatch, isKycDocumentsInvalid, kycDocumentsWatch]);

  return (
    <div className="flex flex-col gap-6">
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.birthNationality')}>
        <CustomColumns columns={[1, 2]}>
          <DatePicker
            selectedDate={selectedDate}
            label={t('pages.dashboard.createUser.form.birthday')}
            todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
            clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
            onChangeDate={(date) => handleDateChange(date, 'birthday')}
            minDate={minimumDate}
            language={language}
            error={
              DateTools.compare(new Date(), selectedDate) === 1
                ? t('pages.dashboard.createUser.form.rules.invalidDate')
                : undefined
            }
          />
          <Select
            label={t('pages.dashboard.createUser.form.birthCountry')}
            options={countryCodeOptions}
            withSearch
            defaultValue={user?.birthCountry}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'birthCountry')}
            showDelete
          />
        </CustomColumns>
        <TwoColumns>
          <Input
            type="text"
            label={t('pages.dashboard.createUser.form.placeOfBirth')}
            {...register('placeOfBirth')}
            onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleInputChange(event, 'placeOfBirth')
            }
          />
          <Select
            label={t('pages.dashboard.createUser.form.nationality')}
            options={countryCodeOptions}
            withSearch
            defaultValue={user?.nationality}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'nationality')}
            showDelete
          />
        </TwoColumns>
        <AdditonalDataLayout
          shouldShow={!!watches['nationalityOther']}
          label={t('pages.dashboard.createUser.form.actions.addOtherNationality')}
        >
          <Select
            label={t('pages.dashboard.createUser.form.nationalityOther')}
            options={countryCodeOptions}
            withSearch
            defaultValue={user?.nationalityOther}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'nationalityOther')}
            showDelete
          />
        </AdditonalDataLayout>
      </ContentLayout>
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.situation')}>
        {config?.settings?.users?.dataQuality?.includes('personalSituation') ? (
          <>
            <Select
              label={t('pages.dashboard.createUser.form.occupation.label')}
              options={occupationCategoryOptions}
              defaultValue={user.occupationCategory}
              onChange={(value: number) => handleValueChange(value, 'occupationCategory')}
              noOptionsComponent={<CreateUserEmptyComponent />}
              tooltipInfo={{
                content: t('pages.dashboard.createUser.form.occupation.tooltip'),
                className: 'min-w-[240px]',
              }}
              withSearch
              showDelete
            />
            <TwoColumns>
              <Select
                label={t('pages.dashboard.createUser.form.personalAssetsEstimated.label')}
                options={personalAssetRanges}
                defaultValue={user.personalAssetsRange}
                onChange={(value: number) => handleValueChange(value, 'personalAssetsRange')}
                noOptionsComponent={<CreateUserEmptyComponent />}
                withSearch
                tooltipInfo={{
                  content: t('pages.dashboard.createUser.form.personalAssetsEstimated.tooltip'),
                  className: 'min-w-[240px]',
                }}
                showDelete
              />
              <Select
                label={t('pages.dashboard.createUser.form.netMontlyIncome.label')}
                options={monthlyIncomeRangeOptions}
                defaultValue={user.monthlyIncomeRange}
                onChange={(value: number) => handleValueChange(value, 'monthlyIncomeRange')}
                noOptionsComponent={<CreateUserEmptyComponent />}
                withSearch
                showDelete
              />
            </TwoColumns>
          </>
        ) : (
          <>
            <TwoColumns>
              <RadioList
                label={t('pages.dashboard.createUser.form.personalAssets.title')}
                options={personalAssetOptions}
                value={watches['personalAssets']}
                onChange={(value: string) => handleValueChange(value, 'personalAssets')}
                className="pt-2 gap-3"
              />
              <RadioList
                label={t('pages.dashboard.createUser.form.incomeRange.title')}
                options={incomeRangeOptions}
                value={watches['incomeRange']}
                onChange={(value: string) => handleValueChange(value, 'incomeRange')}
                className="pt-2 gap-3"
              />
            </TwoColumns>
            <RadioList
              label={t('pages.currentProfiles.profile.financial.occupationType')}
              options={occupationTypeOptions}
              value={watches['occupationType']}
              onChange={(value: string) => handleValueChange(value, 'occupationType')}
              className="pt-2 gap-3"
            />
          </>
        )}
      </ContentLayout>

      <ContentLayout title={t('pages.currentProfiles.profile.taxResidence.cardTitle')}>
        <USResident
          title={t('pages.dashboard.createUser.form.isUsTaxResident')}
          description={t('pages.dashboard.createUser.form.usTaxResidentDescription')}
          value={specifiedUSPersonWatch.toString()}
          handleValuesChange={(changes) => changes.map(({ value, key }) => handleValueChange(value, key))}
          taxResidences={taxResidencesWatch ?? user.taxResidences}
          shouldShowWarning={
            shouldBeUSResident(['country', 'secondaryCountry', 'nationality', 'birthCountry', 'nationalityOther']) &&
            specifiedUSPersonWatch !== '1'
          }
        />
        {taxResidencesWatch?.length || user.taxResidences?.length || !parentUser ? (
          <CreateUserTaxResidences
            taxResidences={taxResidencesWatch ?? user.taxResidences}
            defaultCountry={user?.country}
            onUpdate={(value: ITaxResidenceCreate[]) => handleValueChange(value, 'taxResidences')}
          />
        ) : (
          <div>
            <Button
              variant="ghost"
              icon="add_circle"
              size="s"
              onClick={() => handleValueChange([defaultTaxResidence], 'taxResidences')}
            >
              {!user.roles || user.roles?.includes(EParentType.leader)
                ? t('pages.currentProfiles.profile.taxResidence.form.addItemOptional')
                : t('pages.currentProfiles.profile.taxResidence.form.addItem')}
            </Button>
          </div>
        )}
      </ContentLayout>

      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.documents')} hasDivider={false}>
        <CreateUserKycDocuments
          handleFileChange={(files: TFileWithType[]) => handleValueChange(files, 'kycDocuments')}
          defaultFiles={user?.kycDocuments}
        />
      </ContentLayout>
      <CreateUserFooter
        onNext={handleSubmit}
        isNextDisabled={isFormDisabled}
        onPrev={handleSubmit}
        stepsLength={stepsLength}
      />
    </div>
  );
}
